<template>
  <div class="metrics-summary-bar mb-6" :class="{ 'metrics-summary-bar--preview': previewMode }">
    <div v-if="!loading || previewMode" class="metrics-row">
      <div
        v-for="metric in metricItems"
        :key="metric.key"
        class="metric-card"
        :style="metric.tileStyle"
      >
        <div
          class="metric-card__label"
          :style="metric.labelColor ? { color: metric.labelColor } : {}"
        >
          {{ metric.label }}
        </div>
        <div
          class="metric-card__value"
          :style="metric.labelColor ? { color: metric.labelColor } : {}"
        >
          {{ formatValue(metric.value) }}
        </div>
      </div>
    </div>
    <div v-else class="metrics-row">
      <div v-for="n in skeletonCount" :key="n" class="metric-card">
        <v-skeleton-loader type="text" />
        <v-skeleton-loader type="heading" />
      </div>
    </div>
  </div>
</template>

<script>
const METRIC_DEFINITIONS = {
  "active-programs": { key: "activePrograms", label: "Active Programs" },
  "programs-completed-year": { key: "programsCompletedThisYear", label: "Completed This Year" },
  "points-earned-month": { key: "pointsEarnedThisMonth", label: "Earned This Month" },
  "points-earned-year": { key: "pointsEarnedThisYear", label: "Earned This Year" },
};

const DEFAULT_ORDER = [
  "active-programs",
  "programs-completed-year",
  "points-earned-month",
  "points-earned-year",
];

export default {
  name: "MetricsSummaryBar",
  props: {
    metrics: {
      type: Object,
      default: null,
    },
    metricsConfig: {
      type: Array,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    previewMode: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    configuredMetrics() {
      if (this.metricsConfig && this.metricsConfig.length) {
        return this.metricsConfig;
      }
      return DEFAULT_ORDER.map((metricKey) => ({ metricKey }));
    },
    effectiveMetrics() {
      if (this.previewMode && !this.metrics) {
        return {
          activePrograms: 3,
          programsCompletedThisYear: 1,
          pointsEarnedThisMonth: 500,
          pointsEarnedThisYear: 2400,
        };
      }
      return this.metrics;
    },
    metricItems() {
      return this.configuredMetrics.map((cfg) => {
        const def = METRIC_DEFINITIONS[cfg.metricKey];
        if (!def)
          return { key: cfg.metricKey, label: "—", value: null, tileStyle: {}, labelColor: null };
        const bgColor = cfg.background && cfg.background.color ? cfg.background.color : null;
        const gradient = cfg.background && cfg.background.gradient ? cfg.background.gradient : null;
        const textColor = cfg.textColor || null;
        return {
          key: def.key,
          label: def.label,
          value: this.effectiveMetrics ? this.effectiveMetrics[def.key] : null,
          tileStyle: {
            background: gradient || bgColor || undefined,
            color: textColor || undefined,
          },
          labelColor: textColor || null,
        };
      });
    },
    skeletonCount() {
      return this.configuredMetrics.length;
    },
  },
  methods: {
    formatValue(val) {
      if (val == null) return "—";
      return val;
    },
  },
};
</script>

<style scoped>
.metrics-summary-bar {
  width: 100%;
}

.metrics-summary-bar--preview {
  zoom: 0.55;
}

.metrics-row {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
}

.metric-card {
  /* Mobile-first: 2 tiles per row. nowrap here regressed to a single
     unbreakable row on narrow viewports (see commit 5bd9737c). */
  flex: 1 1 calc(50% - 8px);
  min-width: 0;
  background: #ffffff;
  border: 1px solid rgba(36, 55, 62, 0.12);
  border-radius: 8px;
  box-shadow: 0px 4px 6px rgba(205, 208, 210, 0.24);
  padding: 16px 24px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  text-align: left;
}

@media (min-width: 960px) {
  .metric-card {
    /* Tablet/desktop: all tiles share a single row, as before. */
    flex: 1 1 0;
  }
}

.metric-card__label {
  font-family: "Avenir", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 24px;
  color: rgba(0, 0, 0, 0.87);
  text-align: left;
}

.metric-card__value {
  font-family: "Avenir", sans-serif;
  font-weight: 800;
  font-size: 26px;
  line-height: 32px;
  color: rgba(0, 0, 0, 0.87);
  text-align: left;
}
</style>
